import type { EfacturaConnectionSummary } from '@financiar/shared';
import { useQuery } from '@tanstack/react-query';
import { Select, Skeleton, Space, Tag, Typography } from 'antd';
import { useMemo } from 'react';
import { fetchEfacturaConnections } from '../features/firme/api';
import { useFirmaCurenta } from '../features/firme/FirmaCurentaContext';

/** Small e-Factura status badge for a firm: shows whether it's connected to
 * ANAF and on which environment. Renders nothing when the integration is off
 * globally or no summary is available. */
function FirmEfacturaBadge({ summary }: { summary?: EfacturaConnectionSummary }) {
  if (!summary || !summary.enabled) return null;
  if (summary.expired) return <Tag color="volcano" style={{ marginInlineEnd: 0 }}>e-Factura expirat</Tag>;
  if (summary.connected) {
    return summary.environment === 'prod' ? (
      <Tag color="green" style={{ marginInlineEnd: 0 }}>e-Factura Prod</Tag>
    ) : (
      <Tag color="blue" style={{ marginInlineEnd: 0 }}>e-Factura Test</Tag>
    );
  }
  return <Tag style={{ marginInlineEnd: 0 }}>fără e-Factura</Tag>;
}

/** Header, left side. A single active firm doesn't need a picker — just says
 * which one it is; two or more get a Select. Either way, an e-Factura badge
 * shows the ANAF connection state + environment for the firm(s). */
export function FirmaSelector() {
  const { firma, firme, firmaId, setFirmaId, isLoading } = useFirmaCurenta();

  // One request feeds every firm's badge; invalidated by the firm drawer's
  // e-Factura tab on connect/disconnect/settings changes.
  const { data: connections } = useQuery({
    queryKey: ['efactura', 'connections'],
    queryFn: fetchEfacturaConnections,
    enabled: firme.length > 0,
  });
  const summaryById = useMemo(
    () => new Map((connections ?? []).map((c) => [c.companyId, c])),
    [connections],
  );

  if (isLoading) {
    return <Skeleton.Input active size="small" style={{ width: 200 }} />;
  }

  if (firme.length === 0) {
    return null;
  }

  if (firme.length === 1) {
    return (
      <Space size={8}>
        <Typography.Text strong>{firma?.name}</Typography.Text>
        {firma ? <FirmEfacturaBadge summary={summaryById.get(firma.id)} /> : null}
      </Space>
    );
  }

  return (
    <Select
      value={firmaId ?? undefined}
      style={{ minWidth: 280 }}
      popupMatchSelectWidth={400}
      onChange={(value) => setFirmaId(value)}
      options={firme.map((f) => ({
        value: f.id,
        label: (
          <Space size={8} style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
            <FirmEfacturaBadge summary={summaryById.get(f.id)} />
          </Space>
        ),
      }))}
    />
  );
}
